import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Search, Upload, Image, File, Video, RefreshCw, 
  MoreVertical, Edit3, Trash2, FolderPlus, Download, Move,
  AlertCircle, FolderOpen, FileText, Plus, Folder, ChevronRight,
  Home, Grid, List, Eye, X, Copy, Link2, CheckCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import useS3Upload from '../hooks/useS3Upload';

const ManageAssets = () => {
  const { token } = useAuth();
  const { uploadFile, uploading, uploadProgress } = useS3Upload();
  
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('images');
  const [currentPath, setCurrentPath] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, asset: null });
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
  const [showCreateHospitalFolderModal, setShowCreateHospitalFolderModal] = useState(false);
  const [showCreateClientFolderModal, setShowCreateClientFolderModal] = useState(false);
  const [renameData, setRenameData] = useState({ oldName: '', newName: '' });
  const [deleteData, setDeleteData] = useState({ asset: null, name: '' });
  const [renameFolderData, setRenameFolderData] = useState({ oldName: '', newName: '' });
  const [deleteFolderData, setDeleteFolderData] = useState({ folderName: '', folderPath: '' });
  const [newFolderName, setNewFolderName] = useState('');
  const [moveData, setMoveData] = useState({ asset: null, targetFolder: '' });
  const [availableFolders, setAvailableFolders] = useState([]);
  const [folderContextMenu, setFolderContextMenu] = useState({ show: false, x: 0, y: 0, folder: null });
  
  // Preview modal states
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState({ url: '', name: '', type: '' });
  
  // Copy URL state
  const [copiedUrl, setCopiedUrl] = useState(false);
  
  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // Helper function to check if file is previewable
  const isPreviewable = (fileName) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const documentExtensions = ['.pdf', '.doc', '.docx', '.txt'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    
    return imageExtensions.includes(extension) || documentExtensions.includes(extension);
  };

  // Helper function to get file type for preview
  const getFileType = (fileName) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    
    if (imageExtensions.includes(extension)) return 'image';
    if (['.pdf'].includes(extension)) return 'pdf';
    if (['.doc', '.docx'].includes(extension)) return 'document';
    if (['.txt'].includes(extension)) return 'text';
    return 'unknown';
  };

  // Open preview modal
  const openPreview = (asset) => {
    setPreviewData({
      url: asset.url,
      name: asset.key,
      type: getFileType(asset.key)
    });
    setShowPreviewModal(true);
    setContextMenu({ show: false, x: 0, y: 0, asset: null });
  };

  // Copy URL to clipboard
  const copyUrlToClipboard = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedUrl(true);
      setContextMenu({ show: false, x: 0, y: 0, asset: null });
      setTimeout(() => setCopiedUrl(false), 2000);
    }).catch(err => {
      console.error('Failed to copy URL:', err);
    });
  };

  const assetTypes = [
    { id: 'images', name: 'Images', icon: Image },
    { id: 'documents', name: 'Documents', icon: FileText },
    { id: 'videos', name: 'Videos', icon: Video },
    { id: 'ai-generated', name: 'AI Generated', icon: File }
  ];

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClick = () => setContextMenu({ show: false, x: 0, y: 0, asset: null });
    if (contextMenu.show) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu.show]);

  // Close folder context menu when clicking outside
  useEffect(() => {
    const handleClick = () => setFolderContextMenu({ show: false, x: 0, y: 0, folder: null });
    if (folderContextMenu.show) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [folderContextMenu.show]);

  // Prevent background scrolling when preview modal is open
  useEffect(() => {
    if (showPreviewModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showPreviewModal]);

  // Fetch assets and folders from S3
  const fetchAssets = async (folderType = activeTab, path = currentPath) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('folder_type', folderType);
      params.append('path', path);
      params.append('limit', '1000');

      const response = await fetch(`${API_BASE_URL}/api/s3/files?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAssets(data.files || []);
        setFolders(data.folders || []);
      } else {
        console.error('Failed to fetch assets');
        setAssets([]);
        setFolders([]);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      setAssets([]);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch available folders for move operation
  const fetchAvailableFolders = async (folderType = activeTab) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/s3/folders?folder_type=${folderType}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // The endpoint returns folders directly as an array, not wrapped in an object
        setAvailableFolders(data || []);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
      setAvailableFolders([]);
    }
  };

  // Load assets when component mounts or parameters change
  useEffect(() => {
    fetchAssets(activeTab, currentPath);
  }, [activeTab, currentPath]);

  // Filter assets and folders based on search term
  const filteredAssets = assets.filter(asset => {
    const filename = asset.key.split('/').pop().toLowerCase();
    return filename.includes(searchTerm.toLowerCase());
  });

  const filteredFolders = folders.filter(folder => {
    return folder.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Handle file upload
  const handleFileUpload = async (file) => {
    try {
      // Preserve original filename by using customFilename
      let customFilename;
      if (currentPath) {
        customFilename = `${currentPath}/${file.name}`;
      } else {
        customFilename = file.name;
      }
      
      const result = await uploadFile(file, { 
        folderType: activeTab, 
        customFilename: customFilename 
      });
      
      // Refresh assets
      await fetchAssets(activeTab, currentPath);
      return result;
    } catch (error) {
      console.error('Upload failed:', error);
      throw error;
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev - 1);
    if (dragCounter - 1 === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragCounter(0);
    
    const files = Array.from(e.dataTransfer.files);
    
    // Filter files based on active tab
    let validFiles = files;
    if (activeTab === 'images') {
      validFiles = files.filter(file => file.type.startsWith('image/'));
    } else if (activeTab === 'videos') {
      validFiles = files.filter(file => file.type.startsWith('video/'));
    }
    
    if (validFiles.length === 0) {
      alert(`Please drop ${activeTab} files only`);
      return;
    }
    
    // Upload files one by one
    for (const file of validFiles) {
      try {
        await handleFileUpload(file);
      } catch (error) {
        console.error('Upload failed:', error);
      }
    }
  };

  // Delete asset
  const deleteAsset = async (asset) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/s3/files/${encodeURIComponent(asset.key)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchAssets(activeTab, currentPath);
        setContextMenu({ show: false, x: 0, y: 0, asset: null });
        setShowDeleteModal(false);
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete asset');
    }
  };

  // Rename asset
  const renameAsset = async (oldKey, newName) => {
    try {
      const keyParts = oldKey.split('/');
      keyParts[keyParts.length - 1] = newName;
      const newKey = keyParts.join('/');

      const response = await fetch(`${API_BASE_URL}/api/s3/rename-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          old_key: oldKey,
          new_key: newKey
        })
      });

      if (response.ok) {
        await fetchAssets(activeTab, currentPath);
        setShowRenameModal(false);
        setContextMenu({ show: false, x: 0, y: 0, asset: null });
      } else {
        throw new Error('Rename failed');
      }
    } catch (error) {
      console.error('Rename failed:', error);
      alert('Failed to rename asset');
    }
  };

  // Create folder
  const createFolder = async (folderName) => {
    try {
      const folderPath = currentPath ? `${activeTab}/${currentPath}/${folderName}` : `${activeTab}/${folderName}`;
      
      const response = await fetch(`${API_BASE_URL}/api/s3/create-folder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          folder_path: folderPath
        })
      });

      if (response.ok) {
        await fetchAssets(activeTab, currentPath);
        setShowCreateFolderModal(false);
        setNewFolderName('');
      } else {
        throw new Error('Folder creation failed');
      }
    } catch (error) {
      console.error('Folder creation failed:', error);
      alert('Failed to create folder');
    }
  };

  // Move asset to folder
  const moveAsset = async (asset, targetFolder) => {
    try {
      const keyParts = asset.key.split('/');
      const filename = keyParts[keyParts.length - 1];
      
      // Extract the root path (ah/pvah1000/) from the existing key
      const rootPath = keyParts.slice(0, -2).join('/'); // Remove folder_type and filename, keep root path
      
      // Construct the new key with proper S3 path structure
      let newKey;
      if (targetFolder) {
        // Moving to a specific folder within the activeTab
        newKey = `${rootPath}/${activeTab}/${targetFolder}/${filename}`;
      } else {
        // Moving to root of activeTab
        newKey = `${rootPath}/${activeTab}/${filename}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/s3/rename-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          old_key: asset.key,
          new_key: newKey
        })
      });

      if (response.ok) {
        await fetchAssets(activeTab, currentPath);
        setShowMoveModal(false);
        setContextMenu({ show: false, x: 0, y: 0, asset: null });
      } else {
        throw new Error('Move failed');
      }
    } catch (error) {
      console.error('Move failed:', error);
      alert('Failed to move asset');
    }
  };

  // Delete folder
  const deleteFolder = async (folderName) => {
    try {
      const folderPath = currentPath ? `${activeTab}/${currentPath}/${folderName}` : `${activeTab}/${folderName}`;
      
      const response = await fetch(`${API_BASE_URL}/api/s3/delete-folder`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          folder_path: folderPath
        })
      });

      if (response.ok) {
        await fetchAssets(activeTab, currentPath);
        setShowDeleteFolderModal(false);
        setFolderContextMenu({ show: false, x: 0, y: 0, folder: null });
      } else {
        throw new Error('Folder delete failed');
      }
    } catch (error) {
      console.error('Folder delete failed:', error);
      alert('Failed to delete folder');
    }
  };

  // Rename folder
  const renameFolder = async (oldFolderName, newFolderName) => {
    try {
      const oldFolderPath = currentPath ? `${activeTab}/${currentPath}/${oldFolderName}` : `${activeTab}/${oldFolderName}`;
      const newFolderPath = currentPath ? `${activeTab}/${currentPath}/${newFolderName}` : `${activeTab}/${newFolderName}`;

      const response = await fetch(`${API_BASE_URL}/api/s3/rename-folder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          old_folder_path: oldFolderPath,
          new_folder_path: newFolderPath
        })
      });

      if (response.ok) {
        await fetchAssets(activeTab, currentPath);
        setShowRenameFolderModal(false);
        setFolderContextMenu({ show: false, x: 0, y: 0, folder: null });
      } else {
        throw new Error('Folder rename failed');
      }
    } catch (error) {
      console.error('Folder rename failed:', error);
      alert('Failed to rename folder');
    }
  };

  // Navigate to folder
  const navigateToFolder = (folderName) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    setCurrentPath(newPath);
  };

  // Navigate back
  const navigateBack = () => {
    if (currentPath) {
      const pathParts = currentPath.split('/');
      pathParts.pop();
      setCurrentPath(pathParts.join('/'));
    }
  };

  // Go to root
  const goToRoot = () => {
    setCurrentPath('');
  };

  // Get breadcrumb path
  const getBreadcrumbs = () => {
    if (!currentPath) return [];
    return currentPath.split('/');
  };

  // Get file type icon
  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
      return <Image className="h-6 w-6" />;
    } else if (['mp4', 'avi', 'mov', 'wmv'].includes(ext)) {
      return <Video className="h-6 w-6" />;
    } else {
      return <File className="h-6 w-6" />;
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Success Toast for Copy URL */}
      {copiedUrl && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-fade-in">
          <CheckCircle className="h-5 w-5" />
          <span className="font-medium">URL copied to clipboard!</span>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Manage Assets
                  </h2>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Folder className="h-4 w-4" />
                  <span className="hidden sm:inline">Asset Management</span>
                  <span className="sm:hidden">Assets</span>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Asset Type Tabs */}
            <div className="bg-white rounded-lg shadow-sm mb-6 border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6">
                  {assetTypes.map(type => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => {
                          setActiveTab(type.id);
                          setCurrentPath('');
                        }}
                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                          activeTab === type.id
                            ? 'border-theme-blue text-theme-blue'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{type.name}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

          {/* Controls */}
          <div className="p-6 border-b border-gray-200">
            {activeTab !== 'records' ? (
              // Regular asset management controls
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {/* View Mode Toggle */}
                  <div className="flex border border-gray-300 rounded-lg">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 ${viewMode === 'grid' ? 'bg-blue-100 text-theme-blue' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                      <Grid className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 ${viewMode === 'list' ? 'bg-blue-100 text-theme-blue' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>

                  <button
                    onClick={() => fetchAssets(activeTab, currentPath)}
                    disabled={loading}
                    className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors flex items-center font-medium"
                  >
                    <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>

                  <button
                    onClick={() => setShowCreateFolderModal(true)}
                    className="px-3 py-1.5 text-sm text-green-600 border border-green-300 rounded-md hover:bg-green-50 transition-colors flex items-center font-medium"
                  >
                    <FolderPlus className="h-4 w-4 mr-1.5" />
                    New Folder
                  </button>

                  <label className="px-3 py-1.5 text-sm bg-theme-blue text-white rounded-md hover:bg-blue-700 transition-colors cursor-pointer flex items-center font-medium">
                    <Upload className="h-4 w-4 mr-1.5" />
                    Upload Files
                    <input
                      type="file"
                      multiple
                      accept={activeTab === 'images' ? 'image/*' : activeTab === 'videos' ? 'video/*' : '*/*'}
                      onChange={async (e) => {
                        const files = Array.from(e.target.files);
                        for (const file of files) {
                          try {
                            await handleFileUpload(file);
                          } catch (error) {
                            console.error('Upload failed:', error);
                          }
                        }
                        e.target.value = '';
                      }}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                </div>

                {/* Search */}
                <div className="flex items-center">
                  <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search assets..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            ) : (
              // Records management controls
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {recordsView === 'main' ? (
                    <>
                      <button
                        onClick={() => {
                          fetchClientFolders();
                          fetchHospitalFolders();
                        }}
                        disabled={loading}
                        className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors flex items-center font-medium"
                      >
                        <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                      <button
                        onClick={() => setShowCreateHospitalFolderModal(true)}
                        className="px-3 py-1.5 text-sm text-green-600 border border-green-300 rounded-md hover:bg-green-50 transition-colors flex items-center font-medium"
                      >
                        <FolderPlus className="h-4 w-4 mr-1.5" />
                        New Hospital Folder
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setRecordsView('main');
                          setSelectedClient(null);
                          setSelectedHospitalFolder(null);
                          setRecordFiles([]);
                        }}
                        className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors flex items-center font-medium"
                      >
                        <ArrowLeft className="h-4 w-4 mr-1.5" />
                        Back
                      </button>
                      <button
                        onClick={() => {
                          if (recordsView === 'client') {
                            fetchClientFiles(selectedClient.id);
                          } else if (recordsView === 'hospital') {
                            fetchHospitalFolderFiles(selectedHospitalFolder);
                          }
                        }}
                        disabled={loading}
                        className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors flex items-center font-medium"
                      >
                        <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                      <label className="px-3 py-1.5 text-sm bg-theme-blue text-white rounded-md hover:bg-blue-700 transition-colors cursor-pointer flex items-center font-medium">
                        <Upload className="h-4 w-4 mr-1.5" />
                        Upload Files
                        <input
                          type="file"
                          multiple
                          onChange={async (e) => {
                            const files = Array.from(e.target.files);
                            for (const file of files) {
                              try {
                                if (recordsView === 'client') {
                                  await uploadClientFile(selectedClient.id, file);
                                } else if (recordsView === 'hospital') {
                                  await uploadHospitalFolderFile(selectedHospitalFolder, file);
                                }
                              } catch (error) {
                                console.error('Upload failed:', error);
                              }
                            }
                            e.target.value = '';
                          }}
                          className="hidden"
                        />
                      </label>
                    </>
                  )}
                </div>
                
                <div className="text-sm text-gray-600 font-medium">
                  {recordsView === 'main' ? 'Records Management' : 
                   recordsView === 'client' ? `Client: ${selectedClient?.name}` : 
                   `Hospital: ${selectedHospitalFolder}`}
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {uploading && (
              <div className="mt-4">
                <div className="flex items-center space-x-2 text-sm text-theme-blue">
                  <Upload className="h-4 w-4 animate-pulse" />
                  <span>Uploading to S3... {uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div 
                    className="bg-theme-blue h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Assets Content */}
        <div 
          className={`bg-white rounded-lg shadow-sm relative ${isDragging ? 'ring-4 ring-blue-400 ring-opacity-50' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {/* Drag Overlay */}
          {isDragging && (
            <div className="absolute inset-0 bg-blue-50 bg-opacity-90 z-50 flex flex-col items-center justify-center rounded-lg border-4 border-dashed border-blue-400">
              <Upload className="h-16 w-16 text-theme-blue mb-4 animate-bounce" />
              <p className="text-xl font-semibold text-theme-blue mb-2">Drop files here</p>
              <p className="text-sm text-theme-blue">
                {activeTab === 'images' ? 'Upload images' : activeTab === 'videos' ? 'Upload videos' : 'Upload files'} to this {currentPath ? 'folder' : 'location'}
              </p>
            </div>
          )}
          
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-600">Loading assets...</span>
            </div>
          ) : activeTab === 'records' ? (
            // Records Management View
            <div className="p-6">
              {recordsView === 'main' ? (
                // Main Records View - Show Client and Hospital Folders
                <div className="space-y-8">
                  {/* Client Records Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <FolderOpen className="h-5 w-5 mr-2 text-theme-blue" />
                      Client Records
                    </h3>
                    {clientFolders.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <Folder className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600">No client folders found</p>
                        <p className="text-sm text-gray-500 mt-1">Client folders are auto-created when customers are added</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {clientFolders.map((client) => (
                          <div
                            key={client.id}
                            onClick={() => {
                              setSelectedClient(client);
                              setRecordsView('client');
                              fetchClientFiles(client.id);
                            }}
                            className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:shadow-md cursor-pointer transition-all group"
                          >
                            <FolderOpen className="h-12 w-12 text-theme-blue mb-2 group-hover:text-theme-blue" />
                            <span className="text-sm text-gray-700 text-center truncate w-full font-medium" title={client.name}>
                              {client.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Hospital Records Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Folder className="h-5 w-5 mr-2 text-green-600" />
                      Hospital Records
                    </h3>
                    {hospitalFolders.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <Folder className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600">No hospital folders found</p>
                        <p className="text-sm text-gray-500 mt-1">Create a folder to organize hospital documents</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {hospitalFolders.map((folder) => (
                          <div
                            key={folder.name}
                            className="relative group"
                          >
                            <div
                              onClick={() => {
                                setSelectedHospitalFolder(folder.name);
                                setRecordsView('hospital');
                                fetchHospitalFolderFiles(folder.name);
                              }}
                              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-green-400 hover:shadow-md cursor-pointer transition-all"
                            >
                              <Folder className="h-12 w-12 text-green-500 mb-2 group-hover:text-green-600" />
                              <span className="text-sm text-gray-700 text-center truncate w-full font-medium" title={folder.name}>
                                {folder.name}
                              </span>
                            </div>
                            {/* Delete button */}
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (window.confirm(`Delete folder "${folder.name}" and all its contents?`)) {
                                  await deleteHospitalFolder(folder.name);
                                }
                              }}
                              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-white shadow-md rounded-full p-1 hover:bg-red-50 transition-all z-10"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Files View - Show files in selected folder
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {recordsView === 'client' ? `Client: ${selectedClient?.name}` : `Hospital: ${selectedHospitalFolder}`}
                  </h3>
                  
                  {recordFiles.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">No files found</p>
                      <p className="text-sm text-gray-500 mt-1">Upload some files to get started</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {recordFiles.map((file) => {
                        const filename = file.name;
                        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(filename.split('.').pop().toLowerCase());
                        
                        return (
                          <div
                            key={file.key}
                            className="relative group border border-gray-200 rounded-lg p-3 hover:border-gray-300 hover:shadow-md transition-all"
                          >
                            {/* Delete button */}
                            <button
                              onClick={async () => {
                                if (window.confirm(`Delete file "${filename}"?`)) {
                                  if (recordsView === 'client') {
                                    await deleteClientFile(selectedClient.id, file.key);
                                  } else if (recordsView === 'hospital') {
                                    await deleteHospitalFolderFile(selectedHospitalFolder, file.key);
                                  }
                                }
                              }}
                              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-white shadow-md rounded-full p-1 hover:bg-red-50 transition-all z-10"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </button>
                            
                            {/* Preview button */}
                            {isPreviewable(filename) && (
                              <button
                                onClick={() => openPreview(file)}
                                className="absolute top-2 right-10 opacity-0 group-hover:opacity-100 bg-white shadow-md rounded-full p-1 hover:bg-blue-50 transition-all z-10"
                              >
                                <Eye className="h-4 w-4 text-theme-blue" />
                              </button>
                            )}
                            
                            {/* File preview */}
                            <div className="aspect-square bg-gray-100 rounded-lg mb-2 overflow-hidden flex items-center justify-center">
                              {isImage ? (
                                <img 
                                  src={file.url} 
                                  alt={filename}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="text-gray-400">
                                  {getFileIcon(filename)}
                                </div>
                              )}
                            </div>
                            
                            {/* File info */}
                            <div className="text-xs space-y-1">
                              <p className="font-medium text-gray-800 truncate" title={filename}>
                                {filename}
                              </p>
                              <p className="text-gray-500">
                                {formatFileSize(file.size)}
                              </p>
                              <button
                                onClick={() => copyUrlToClipboard(file.url)}
                                className="text-theme-blue hover:text-blue-700 flex items-center space-x-1"
                              >
                                <Copy className="h-3 w-3" />
                                <span>Copy URL</span>
                              </button>
                              <a
                                href={file.url}
                                download
                                className="text-green-600 hover:text-green-700 flex items-center space-x-1"
                              >
                                <Download className="h-3 w-3" />
                                <span>Download</span>
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            // Regular Assets View
            <div className="p-6">
              {/* Breadcrumb Navigation (always show when in subfolder) */}
              {currentPath && (
                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
                  <button
                    onClick={goToRoot}
                    className="hover:text-theme-blue flex items-center space-x-1"
                  >
                    <Home className="h-4 w-4" />
                    <span>{activeTab}</span>
                  </button>
                  {getBreadcrumbs().map((crumb, index) => (
                    <React.Fragment key={index}>
                      <ChevronRight className="h-4 w-4" />
                      <button
                        onClick={() => {
                          const pathParts = getBreadcrumbs().slice(0, index + 1);
                          setCurrentPath(pathParts.join('/'));
                        }}
                        className="hover:text-theme-blue"
                      >
                        {crumb}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              )}

              {/* Drag & Drop Hint Banner */}
              {(filteredAssets.length > 0 || filteredFolders.length > 0) && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-2 text-sm">
                    <Upload className="h-4 w-4 text-theme-blue flex-shrink-0" />
                    <p className="text-blue-800">
                      <span className="font-medium">Tip:</span> Drag and drop files here to upload them quickly
                    </p>
                  </div>
                </div>
              )}

              {/* Folders */}
              {filteredFolders.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Folders</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {filteredFolders.map((folder, index) => (
                      <div
                        key={index}
                        onClick={() => navigateToFolder(folder)}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setFolderContextMenu({
                            show: true,
                            x: e.clientX,
                            y: e.clientY,
                            folder: folder
                          });
                        }}
                        className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-md cursor-pointer transition-all group relative"
                      >
                        {/* Management menu button for managers */}
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFolderContextMenu({
                                show: true,
                                x: e.clientX,
                                y: e.clientY,
                                folder: folder
                              });
                            }}
                            className="bg-white shadow-md rounded-full p-1 hover:bg-gray-100 transition-colors"
                          >
                            <MoreVertical className="h-4 w-4 text-gray-600" />
                          </button>
                        </div>
                        
                        <Folder className="h-12 w-12 text-theme-blue mb-2" />
                        <span className="text-sm text-gray-700 text-center truncate w-full" title={folder}>
                          {folder}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Assets */}
              {filteredAssets.length === 0 && filteredFolders.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <Upload className="h-16 w-16 mb-4 text-gray-400" />
                  <p className="text-lg font-medium text-gray-700">No assets found</p>
                  <p className="text-sm text-gray-600 mb-2">Upload some files to get started</p>
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg max-w-md">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <Upload className="h-5 w-5 text-theme-blue mt-0.5" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-blue-900">Drag & Drop Files</p>
                        <p className="text-xs text-blue-700 mt-1">
                          Simply drag files from your computer and drop them here to upload instantly
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : filteredAssets.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    Assets ({filteredAssets.length})
                  </h3>
                  
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {filteredAssets.map((asset) => {
                        const filename = asset.key.split('/').pop();
                        const isImage = activeTab === 'images';
                        
                        return (
                          <div
                            key={asset.key}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              setContextMenu({
                                show: true,
                                x: e.clientX,
                                y: e.clientY,
                                asset: asset
                              });
                            }}
                            className="relative group border border-gray-200 rounded-lg p-3 hover:border-gray-300 hover:shadow-md transition-all"
                          >
                            {/* Management menu button */}
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setContextMenu({
                                    show: true,
                                    x: e.clientX,
                                    y: e.clientY,
                                    asset: asset
                                  });
                                }}
                                className="bg-white shadow-md rounded-full p-1 hover:bg-gray-100 transition-colors"
                              >
                                <MoreVertical className="h-4 w-4 text-gray-600" />
                              </button>
                            </div>
                            
                            {/* Preview */}
                            <div className="aspect-square bg-gray-100 rounded-lg mb-2 overflow-hidden flex items-center justify-center">
                              {isImage ? (
                                <img 
                                  src={asset.url} 
                                  alt={filename}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : (
                                <div className="text-gray-400">
                                  {getFileIcon(filename)}
                                </div>
                              )}
                              
                              {/* Fallback for broken images */}
                              <div className="w-full h-full items-center justify-center text-gray-400 hidden">
                                {getFileIcon(filename)}
                              </div>
                            </div>
                            
                            {/* File info */}
                            <div className="text-xs space-y-1">
                              <p className="font-medium text-gray-800 truncate" title={filename}>
                                {filename}
                              </p>
                              <p className="text-gray-500">
                                {formatFileSize(asset.size)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredAssets.map((asset) => {
                        const filename = asset.key.split('/').pop();
                        
                        return (
                          <div
                            key={asset.key}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              setContextMenu({
                                show: true,
                                x: e.clientX,
                                y: e.clientY,
                                asset: asset
                              });
                            }}
                            className="flex items-center space-x-4 p-3 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all group"
                          >
                            <div className="flex-shrink-0">
                              {getFileIcon(filename)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {filename}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(asset.size)}
                              </p>
                            </div>
                            <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setContextMenu({
                                    show: true,
                                    x: e.clientX,
                                    y: e.clientY,
                                    asset: asset
                                  });
                                }}
                                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu.show && contextMenu.asset && (
        <div
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50"
          style={{ 
            left: `${contextMenu.x}px`, 
            top: `${contextMenu.y}px`,
            transform: 'translate(-50%, 0)'
          }}
        >
          <button
            onClick={() => {
              setRenameData({
                oldName: contextMenu.asset.key.split('/').pop(),
                newName: contextMenu.asset.key.split('/').pop()
              });
              setShowRenameModal(true);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
          >
            <Edit3 className="h-4 w-4 mr-2" />
            Rename
          </button>
          <button
            onClick={() => {
              setMoveData({ asset: contextMenu.asset, targetFolder: '' });
              fetchAvailableFolders(activeTab);
              setShowMoveModal(true);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
          >
            <Move className="h-4 w-4 mr-2" />
            Move
          </button>
          {isPreviewable(contextMenu.asset.key) && (
            <button
              onClick={() => openPreview(contextMenu.asset)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </button>
          )}
          <button
            onClick={() => copyUrlToClipboard(contextMenu.asset.url)}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy URL
          </button>
          <button
            onClick={() => {
              setDeleteData({
                asset: contextMenu.asset,
                name: contextMenu.asset.key.split('/').pop()
              });
              setShowDeleteModal(true);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-red-600 flex items-center"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </button>
          <button
            onClick={() => {
              window.open(contextMenu.asset.url, '_blank');
              setContextMenu({ show: false, x: 0, y: 0, asset: null });
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
          >
            <Download className="h-4 w-4 mr-2" />
            Download
          </button>
        </div>
      )}

      {/* Folder Context Menu */}
      {folderContextMenu.show && folderContextMenu.folder && (
        <div
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50"
          style={{ 
            left: `${folderContextMenu.x}px`, 
            top: `${folderContextMenu.y}px`,
            transform: 'translate(-50%, 0)'
          }}
        >
          <button
            onClick={() => {
              setRenameFolderData({
                oldName: folderContextMenu.folder,
                newName: folderContextMenu.folder
              });
              setShowRenameFolderModal(true);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
          >
            <Edit3 className="h-4 w-4 mr-2" />
            Rename Folder
          </button>
          <button
            onClick={() => {
              const currentFolderPath = currentPath ? `${activeTab}/${currentPath}/${folderContextMenu.folder}` : `${activeTab}/${folderContextMenu.folder}`;
              setDeleteFolderData({
                folderName: folderContextMenu.folder,
                folderPath: currentFolderPath
              });
              setShowDeleteFolderModal(true);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-red-600 flex items-center"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Folder
          </button>
        </div>
      )}

      {/* Modals */}
      {/* Rename Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Rename Asset</h3>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New filename
              </label>
              <input
                type="text"
                value={renameData.newName}
                onChange={(e) => setRenameData(prev => ({ ...prev, newName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter new filename"
              />
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowRenameModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => renameAsset(contextMenu.asset.key, renameData.newName)}
                disabled={!renameData.newName.trim()}
                className="px-4 py-2 text-white bg-theme-blue rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteData.asset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center">
                <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Delete Asset</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600">
                Are you sure you want to delete <strong>{deleteData.name}</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteAsset(deleteData.asset)}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center">
                <FolderPlus className="h-6 w-6 text-green-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Create New Folder</h3>
              </div>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Folder name
              </label>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Enter folder name"
              />
              <p className="text-xs text-gray-500 mt-2">
                Folder will be created in: {activeTab}{currentPath ? `/${currentPath}` : ''}/{newFolderName}
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCreateFolderModal(false);
                  setNewFolderName('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => createFolder(newFolderName)}
                disabled={!newFolderName.trim()}
                className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                Create Folder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Modal */}
      {showMoveModal && moveData.asset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center">
                <Move className="h-6 w-6 text-theme-blue mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Move Asset</h3>
              </div>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select destination folder
              </label>
              <select
                value={moveData.targetFolder}
                onChange={(e) => setMoveData(prev => ({ ...prev, targetFolder: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Root ({activeTab})</option>
                {availableFolders.map((folder, index) => (
                  <option key={index} value={folder}>
                    {folder}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Moving: {moveData.asset.key.split('/').pop()}
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowMoveModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => moveAsset(moveData.asset, moveData.targetFolder)}
                className="px-4 py-2 text-white bg-theme-blue rounded-lg hover:bg-blue-700 transition-colors"
              >
                Move
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {showRenameFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Rename Folder</h3>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New folder name
              </label>
              <input
                type="text"
                value={renameFolderData.newName}
                onChange={(e) => setRenameFolderData(prev => ({ ...prev, newName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter new folder name"
              />
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowRenameFolderModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => renameFolder(renameFolderData.oldName, renameFolderData.newName)}
                disabled={!renameFolderData.newName.trim() || renameFolderData.newName === renameFolderData.oldName}
                className="px-4 py-2 text-white bg-theme-blue rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Folder Confirmation Modal */}
      {showDeleteFolderModal && deleteFolderData.folderName && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center">
                <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Delete Folder</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600">
                Are you sure you want to delete the folder <strong>"{deleteFolderData.folderName}"</strong> and all its contents? 
              </p>
              <p className="text-red-600 text-sm mt-2 font-medium">
                This action cannot be undone and will permanently delete all files and subfolders within it.
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteFolderModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteFolder(deleteFolderData.folderName)}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Folder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Preview: {previewData.name.split('/').pop()}</h3>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 max-h-[calc(90vh-120px)] overflow-auto">
              {previewData.type === 'image' && (
                <div className="text-center">
                  <img 
                    src={previewData.url} 
                    alt={previewData.name}
                    className="max-w-full h-auto rounded-lg shadow-lg"
                    style={{ maxHeight: 'calc(90vh - 200px)' }}
                  />
                </div>
              )}

              {previewData.type === 'pdf' && (
                <div className="w-full" style={{ height: 'calc(90vh - 200px)' }}>
                  <iframe
                    src={`${previewData.url}#view=FitH`}
                    className="w-full h-full border border-gray-300 rounded-lg"
                    title={`Preview of ${previewData.name}`}
                  />
                </div>
              )}

              {previewData.type === 'document' && (
                <div className="text-center py-8">
                  <FileText className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Document Preview</h4>
                  <p className="text-gray-600 mb-4">
                    Preview is not available for this document type. You can download it to view the content.
                  </p>
                  <div className="space-x-2">
                    <button
                      onClick={() => window.open(previewData.url, '_blank')}
                      className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Download className="h-4 w-4 inline mr-2" />
                      Download
                    </button>
                    <button
                      onClick={() => window.open(`https://docs.google.com/gview?url=${encodeURIComponent(previewData.url)}&embedded=true`, '_blank')}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Eye className="h-4 w-4 inline mr-2" />
                      View in Google Docs
                    </button>
                  </div>
                </div>
              )}

              {previewData.type === 'text' && (
                <div className="w-full" style={{ height: 'calc(90vh - 200px)' }}>
                  <iframe
                    src={previewData.url}
                    className="w-full h-full border border-gray-300 rounded-lg bg-white"
                    title={`Preview of ${previewData.name}`}
                  />
                </div>
              )}

              {previewData.type === 'unknown' && (
                <div className="text-center py-8">
                  <AlertCircle className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Preview Not Available</h4>
                  <p className="text-gray-600 mb-4">
                    Preview is not supported for this file type.
                  </p>
                  <button
                    onClick={() => window.open(previewData.url, '_blank')}
                    className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Download className="h-4 w-4 inline mr-2" />
                    Download
                  </button>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-between items-center p-4 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                File: {previewData.name.split('/').pop()}
              </div>
              <div className="space-x-2">
                <button
                  onClick={() => window.open(previewData.url, '_blank')}
                  className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                  <Download className="h-3 w-3 inline mr-1" />
                  Download
                </button>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Hospital Folder Modal */}
      {showCreateHospitalFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Create Hospital Folder</h3>
              <button
                onClick={() => {
                  setShowCreateHospitalFolderModal(false);
                  setNewHospitalFolderName('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Folder Name
              </label>
              <input
                type="text"
                value={newHospitalFolderName}
                onChange={(e) => setNewHospitalFolderName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    createHospitalFolder(newHospitalFolderName);
                    setShowCreateHospitalFolderModal(false);
                    setNewHospitalFolderName('');
                  }
                }}
                placeholder="Enter folder name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <p className="mt-2 text-xs text-gray-500">
                Spaces will be converted to underscores. Only letters, numbers, hyphens, and underscores are allowed.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowCreateHospitalFolderModal(false);
                  setNewHospitalFolderName('');
                }}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (newHospitalFolderName.trim()) {
                    await createHospitalFolder(newHospitalFolderName);
                    setShowCreateHospitalFolderModal(false);
                    setNewHospitalFolderName('');
                  }
                }}
                disabled={!newHospitalFolderName.trim()}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Create Folder
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default ManageAssets;